/**
 * Builders for schema.org JSON-LD on public detail pages (jobs, projects, profiles,
 * courses, blog, discussions). Pages inject the result with `useJsonLd`.
 *
 * Only put data in here that the page itself renders publicly — structured data is
 * read by every crawler and must never carry more than the visible page does.
 */

export const SITE_URL = 'https://growni.sk'
export const SITE_NAME = 'GROWNi.sk'

export type JsonLdNode = Record<string, unknown>

export type BreadcrumbItem = {
    name: string
    /** Site path (`/praca`) or absolute URL. */
    path: string
}

/** Fallback share images per section (1200×630, served from /public). */
export const SECTION_SOCIAL_IMAGES = {
    jobs: `${SITE_URL}/images/social/growni-miesto-pre-tvoj-rast-pracovne-ponuky.jpeg`,
    mentoring: `${SITE_URL}/images/social/growni-miesto-pre-tvoj-rast-mentoring.jpg`,
    volunteers: `${SITE_URL}/images/social/growni-miesto-pre-tvoj-rast-dobrovolnici.jpg`,
    volunteeringProjects: `${SITE_URL}/images/social/growni-miesto-pre-tvoj-rast-dobrovolnicke-projekty.jpg`,
    volunteering: `${SITE_URL}/images/social/growni-miesto-pre-tvoj-rast-dobrovolnictvo.jpg`,
    skills: `${SITE_URL}/images/social/growni-miesto-pre-tvoj-rast-naskillujsa-eventy-kurzy.jpg`,
    blog: `${SITE_URL}/images/social/growni-miesto-pre-tvoj-rast-blog.jpg`,
    selfKnowledge: `${SITE_URL}/images/social/growni-miesto-pre-tvoj-rast-sebapoznanie.png`
} as const

/** GROWNi as the publisher of its own content (blog, discussions). Logo from the media kit. */
export const GROWNI_ORGANIZATION: JsonLdNode = {
    '@type': 'Organization',
    name: 'GROWNi',
    url: SITE_URL,
    logo: {
        '@type': 'ImageObject',
        url: `${SITE_URL}/media/growni-media-kit/growni-logo-navy.png`,
        width: 2000,
        height: 398
    }
}

/** Collapses runs of whitespace (incl. non-breaking spaces) and trims. */
export function collapseWhitespace(value: string | null | undefined): string {
    return (value ?? '').replace(/[\s ]+/g, ' ').trim()
}

const HTML_ENTITIES: Record<string, string> = {
    amp: '&',
    lt: '<',
    gt: '>',
    quot: '"',
    apos: "'",
    nbsp: ' '
}

function decodeHtmlEntities(value: string): string {
    return value.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (entity, code: string) => {
        if (code[0] === '#') {
            const point = code[1] === 'x' || code[1] === 'X' ? parseInt(code.slice(2), 16) : parseInt(code.slice(1), 10)
            return Number.isFinite(point) ? String.fromCodePoint(point) : entity
        }
        return HTML_ENTITIES[code.toLowerCase()] ?? entity
    })
}

/** Escapes text for use inside an HTML description (e.g. JobPosting.description). */
export function escapeHtml(value: string): string {
    return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

/**
 * Turns rich-text HTML into plain text, cut at a word boundary to `maxLength`
 * characters (ellipsis included). Block ends become spaces so words don't glue together.
 */
export function htmlToPlainText(html: string | null | undefined, maxLength?: number): string {
    const text = collapseWhitespace(
        decodeHtmlEntities(
            (html ?? '')
                .replace(/<(br|\/p|\/div|\/li|\/h[1-6])\b[^>]*>/gi, ' ')
                .replace(/<[^>]*>/g, '')
        )
    )
    return maxLength ? truncateText(text, maxLength) : text
}

/** Cuts text at the last word boundary that fits `maxLength` (ellipsis included). */
export function truncateText(text: string, maxLength: number): string {
    if (text.length <= maxLength) return text
    const cut = text.slice(0, maxLength - 1)
    const lastSpace = cut.lastIndexOf(' ')
    const base = lastSpace > maxLength * 0.6 ? cut.slice(0, lastSpace) : cut
    return `${base.replace(/[\s,.;:–-]+$/, '')}…`
}

/**
 * Joins sentences into a meta description of at most `maxLength` characters. The
 * first part is always kept (truncated if needed); later parts are added while they fit,
 * the last one possibly truncated.
 */
export function composeDescription(parts: Array<string | null | undefined | false>, maxLength = 160): string {
    let result = ''
    for (const raw of parts) {
        const part = collapseWhitespace(raw || '')
        if (!part) continue
        const candidate = result ? `${result} ${part}` : part
        if (candidate.length <= maxLength) {
            result = candidate
            continue
        }
        const room = maxLength - (result ? result.length + 1 : 0)
        // Don't append a stub of a sentence — only truncate when there is real room left.
        if (!result || room >= 40) {
            result = result ? `${result} ${truncateText(part, room)}` : truncateText(part, maxLength)
        }
        break
    }
    return result
}

/** Upper-cases the first letter (user-entered titles are sometimes all lowercase). */
export function capitalizeFirst(value: string): string {
    return value ? value.charAt(0).toLocaleUpperCase('sk') + value.slice(1) : value
}

/** `<name> | GROWNi.sk`, without doubling the brand when the name already carries it. */
export function withSiteName(title: string): string {
    const clean = collapseWhitespace(title)
    if (!clean) return SITE_NAME
    return /growni/i.test(clean) ? clean : `${clean} | ${SITE_NAME}`
}

/** Absolute URL on growni.sk for a site path; absolute URLs pass through. */
export function absoluteUrl(pathOrUrl: string): string {
    if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl
    return `${SITE_URL}${pathOrUrl.startsWith('/') ? '' : '/'}${pathOrUrl}`
}

/**
 * Normalizes a user-entered website (`www.example.sk `) to an absolute http(s) URL,
 * or `undefined` when it is not a usable URL.
 */
export function normalizeWebsiteUrl(value: string | null | undefined): string | undefined {
    const trimmed = (value ?? '').trim()
    if (!trimmed || /\s/.test(trimmed)) return undefined
    const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
    try {
        const url = new URL(withProtocol)
        return url.hostname.includes('.') ? url.toString() : undefined
    } catch {
        return undefined
    }
}

/**
 * ISO 8601 string from the date shapes the app passes around: Date, epoch millis,
 * ISO strings (API-serialized) and Firestore timestamps (client or admin SDK).
 */
export function toIsoDateTime(value: unknown): string | undefined {
    if (value == null || value === '') return undefined
    let date: Date | undefined
    if (value instanceof Date) {
        date = value
    } else if (typeof value === 'number' || typeof value === 'string') {
        date = new Date(value)
    } else if (typeof value === 'object') {
        const timestamp = value as { toDate?: () => Date; seconds?: number; _seconds?: number }
        if (typeof timestamp.toDate === 'function') date = timestamp.toDate()
        else if (typeof timestamp.seconds === 'number') date = new Date(timestamp.seconds * 1000)
        else if (typeof timestamp._seconds === 'number') date = new Date(timestamp._seconds * 1000)
    }
    return date && Number.isFinite(date.getTime()) ? date.toISOString() : undefined
}

/** Free-text location values that name no town ("Na dohode", "Kraje Slovenska", "online"). */
const NON_LOCALITY_PATTERN =
    /dohod|\bkraj[ea]?\b|\bslovensk[oau]\b|online|remote|z domu|celé|celá|celý|všade|všetky|kdekoľvek|https?:|\d/i
/** "Brussels (Belgium)" style entries of the foreign-city picker. */
const FOREIGN_CITY_PATTERN = /^(.+?)\s*\(([^)]+)\)$/

/** "Bratislava - Dúbravka" → "Bratislava", "KOŠICE" → "Košice"; the town is what Google can place. */
function normalizeLocality(value: string): string {
    const town = collapseWhitespace(value).split(/\s+[-–]\s+/)[0] ?? ''
    if (town.length > 3 && town === town.toLocaleUpperCase('sk')) {
        return town.toLocaleLowerCase('sk').replace(/(^|\s)\S/g, (letter) => letter.toLocaleUpperCase('sk'))
    }
    return town
}

/**
 * `jobLocation` places for a JobPosting from the locations a job or project lists.
 * Unknown entries are treated as Slovak towns (the location pickers allow custom towns) in
 * `region` when given; entries that name no town are dropped, and `countryNames` entries
 * become country-only.
 */
export function buildJobLocations(
    locations: string[] | undefined,
    { countryNames = [], region }: { countryNames?: string[]; region?: string } = {}
): JsonLdNode[] {
    const countries = new Set(countryNames)
    const places = (locations ?? [])
        .map((location) => normalizeLocality(location))
        .filter((location) => location && !NON_LOCALITY_PATTERN.test(location))
        .map((location) => {
            if (countries.has(location)) return { addressCountry: location }
            const foreign = FOREIGN_CITY_PATTERN.exec(location)
            if (foreign) return { addressLocality: foreign[1], addressCountry: foreign[2] }
            return { addressLocality: capitalizeFirst(location), addressRegion: region, addressCountry: 'SK' }
        })
    const unique = new Map(places.map((address) => [JSON.stringify(address), address]))
    return [...unique.values()].map((address) => ({ '@type': 'Place', address: { '@type': 'PostalAddress', ...address } }))
}

/**
 * Remote work for JobPosting: Google requires at least one country applicants may work
 * from. GROWNi is a Slovak board, so remote offers are marked as open to Slovakia.
 */
export const REMOTE_JOB_IN_SLOVAKIA: JsonLdNode = {
    jobLocationType: 'TELECOMMUTE',
    applicantLocationRequirements: { '@type': 'Country', name: 'SK' }
}

export type PublicPersonProfile = {
    name: string
    /** Canonical URL of the profile page. */
    url: string
    image?: string
    /** Short bio as shown on the page (plain text). */
    description?: string
    jobTitle?: string
    worksFor?: string
    knowsAbout?: string[]
    dateModified?: string
}

/**
 * The job a profile page shows as the current one (first in the page's order), but only
 * when it is ongoing — a finished job would be a wrong `jobTitle`.
 */
export function getOngoingJob<T extends { endYear?: unknown; position?: string; org?: string }>(
    jobs: T[] | undefined
): T | undefined {
    return (jobs ?? []).find((job) => !job.endYear && (job.position || job.org))
}

/**
 * ProfilePage + Person for a public profile. Callers pass only what the page renders and
 * only for profiles that are public — never for hidden, private or TOP-only profiles.
 */
export function buildProfilePage(person: PublicPersonProfile): JsonLdNode {
    return {
        '@type': 'ProfilePage',
        url: person.url,
        dateModified: person.dateModified,
        mainEntity: {
            '@type': 'Person',
            name: person.name,
            url: person.url,
            image: person.image,
            description: person.description,
            jobTitle: person.jobTitle,
            worksFor: person.worksFor ? { '@type': 'Organization', name: person.worksFor } : undefined,
            knowsAbout: person.knowsAbout?.map((topic) => collapseWhitespace(topic)).filter(Boolean)
        }
    }
}

/** BreadcrumbList for the trail shown on (or implied by) the page; the last item is the page. */
export function buildBreadcrumbList(items: BreadcrumbItem[]): JsonLdNode {
    return {
        '@type': 'BreadcrumbList',
        itemListElement: items
            .filter((item) => collapseWhitespace(item.name))
            .map((item, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                name: collapseWhitespace(item.name),
                item: absoluteUrl(item.path)
            }))
    }
}

function isEmptyValue(value: unknown): boolean {
    if (value === undefined || value === null || value === '') return true
    if (Array.isArray(value)) return value.length === 0
    if (typeof value === 'object') return Object.keys(value as object).length === 0
    return typeof value === 'number' && !Number.isFinite(value)
}

/**
 * Drops empty values (undefined, null, '', NaN, empty arrays/objects) recursively, so
 * builders can list optional properties without a conditional around each one.
 * A node left with nothing but its `@type` is dropped as well.
 */
export function pruneJsonLd<T>(value: T): T | undefined {
    if (Array.isArray(value)) {
        const items = value.map((item) => pruneJsonLd(item)).filter((item) => !isEmptyValue(item))
        return (items.length ? items : undefined) as T | undefined
    }
    if (value && typeof value === 'object' && !(value instanceof Date)) {
        const entries = Object.entries(value as Record<string, unknown>)
            .map(([key, item]) => [key, pruneJsonLd(item)] as const)
            .filter(([, item]) => !isEmptyValue(item))
        if (!entries.length || entries.every(([key]) => key === '@type')) return undefined
        return Object.fromEntries(entries) as T
    }
    return isEmptyValue(value) ? undefined : value
}
